import { useCallback, useMemo } from "react";

import * as Constants from "../constants";

import type * as Repository from "../../../_repository";
import type * as Type from "../type";

type Props = {
	// オプションカラーリスト
	optionPmsColorListQueryData: Repository.Query.QueryData.OptionPmsColorListQueryData;
	// オプションリスト
	optionPmsActiveListQueryData: Repository.Query.QueryData.OptionPmsList.OptionPmsActiveListQueryData;
	// 選択したオプション
	viewModel: Type.ViewModel;
};
type Result = {
	viewData: Type.ViewData;
};

type OptionWithStartAndEnd = Repository.Query.QueryData.OptionPmsList.Option & {
	optionStartAt: string;
	optionEndAt: string;
};

type OptionWithStartAndEndLeft = OptionWithStartAndEnd & {
	left: number;
};

export const useViewDataConverter = ({
	optionPmsColorListQueryData: {
		optionPmsColorList: { optionColors },
	},
	optionPmsActiveListQueryData: {
		optionPmsList: { options },
	},
	viewModel: { selectedCardID, groupByOptionID },
}: Props): Result => {
	/**
	 * timeAt を ":" で分割する
	 */
	const onSplitTime = useCallback((timeAt?: string | null) => {
		return timeAt?.split(":") ?? ["00", "00"];
	}, []);

	/**
	 * 2つの時間の差を計算する
	 */
	const onCalcDiffHours = useCallback(
		(a: string, b: string) => {
			const [aHour, aMinute] = onSplitTime(a);
			const [bHour, bMinute] = onSplitTime(b);

			const hour = Number(aHour) - Number(bHour);
			const minute = Number(aMinute) - Number(bMinute);
			return Math.abs(hour + minute / 60);
		},
		[onSplitTime],
	);

	/**
	 * optionStartAt を基準にソートする
	 */
	const onSortOptionStartAt = useCallback(
		(aOptionStartAt?: string | null, bOptionStartAt?: string | null) => {
			/**
			 * @example
			 * ```
			 * optionStartAt = 09:00
			 * ["09", "00"]
			 * ````
			 */
			const [aTimeHour, aTimeMinute] = onSplitTime(aOptionStartAt);
			const [bTimeHour, bTimeMinute] = onSplitTime(bOptionStartAt);
			/**
			 * @example
			 * ```
			 * aTime = ["09", "00"] -> "09" + "00" = "0900" -> 900
			 *
			 * ```
			 */
			const aTimeNum = Number(aTimeHour + aTimeMinute);
			const bTimeNum = Number(bTimeHour + bTimeMinute);

			return aTimeNum - bTimeNum;
		},
		[onSplitTime],
	);

	/**
	 * optionStartAt と optionEndAt が存在するものだけの option
	 */
	const filteredOptionList = useMemo(
		() => options.filter((option) => option.optionStartAt && option.optionEndAt) as OptionWithStartAndEndLeft[],
		[options],
	);

	const firstOptionStartAt = useMemo(
		() =>
			filteredOptionList.sort((a, b) => onSortOptionStartAt(a.optionStartAt, b.optionStartAt)).at(0)
				?.optionStartAt,
		[onSortOptionStartAt, filteredOptionList],
	);
	const lastOptionEndAt = useMemo(
		() => filteredOptionList.sort((a, b) => onSortOptionStartAt(a.optionEndAt, b.optionEndAt)).at(-1)?.optionEndAt,
		[onSortOptionStartAt, filteredOptionList],
	);

	/**
	 * height の計算を行う
	 */
	const onCalcHeightForOptionPanelItem = useCallback(
		(startTimeAt?: string | null, endTimeAt?: string | null): number => {
			/**
			 * @example
			 * ```
			 * optionStartAt = 09:15
			 * ["09", "15"]
			 * ````
			 */
			const [startTimeHour, startTimeMinute] = onSplitTime(startTimeAt);
			/**
			 * @example
			 * ```
			 * optionEndAt = 14:23
			 * ["14", "23"]
			 * ````
			 */
			const [endTimeHour, endTimeMinute] = onSplitTime(endTimeAt);

			/**
			 * @example
			 * ```
			 * startTimeNum = 9 * 60 + 15 = 540 + 15 = 555
			 * ```
			 */
			const startTimeNum = Number(startTimeHour) * Constants.PerOneHour + Number(startTimeMinute);
			/**
			 * @example
			 * ```
			 * endTimeNum = 14 * 60 + 23 = 840 + 23 = 863
			 * ```
			 */
			const endTimeNum = Number(endTimeHour) * Constants.PerOneHour + Number(endTimeMinute);

			/**
			 * @example
			 * ```
			 * height = 863 - 555 = 308
			 * ```
			 */
			return endTimeNum - startTimeNum;
		},
		[onSplitTime],
	);
	/**
	 * top の計算を行う
	 */
	const onCalcTopForOptionPanelItem = useCallback(
		(timeAt?: string | null): number => {
			/**
			 * @example
			 * ```
			 * optionStartAt = 09:00
			 * ["09", "00"]
			 * ````
			 */
			const [timeHour, timeMinute] = onSplitTime(timeAt);
			// オプションの最初の開始時間
			const [firstOptionStartAtHour] = onSplitTime(firstOptionStartAt);

			let top = 0;
			/**
			 * @example
			 * ```
			 * time = ["09", "00"]
			 * top = 9 * 60 = 540
			 * top = 540 + 0 * 1 = 540
			 * ```
			 */
			top += (Number(timeHour) - Number(firstOptionStartAtHour)) * Constants.PerOneHour;
			top += Number(timeMinute);

			return top;
		},
		[firstOptionStartAt, onSplitTime],
	);

	/**
	 * optionID -> グループ並び順インデックス
	 * 各グループ（=同じ optionID）の最早 optionStartAt が早い順に左から並べる。
	 */
	const optionIDGroupIndexMap = useMemo(() => {
		const earliestStartByOptionID = new Map<string, string>();
		for (const option of filteredOptionList) {
			const current = earliestStartByOptionID.get(option.optionID);
			if (!current || onSortOptionStartAt(option.optionStartAt, current) < 0) {
				earliestStartByOptionID.set(option.optionID, option.optionStartAt);
			}
		}
		const sortedOptionIDs = Array.from(earliestStartByOptionID.entries())
			.sort(([, aStart], [, bStart]) => onSortOptionStartAt(aStart, bStart))
			.map(([optionID]) => optionID);
		return new Map(sortedOptionIDs.map((optionID, index) => [optionID, index]));
	}, [filteredOptionList, onSortOptionStartAt]);

	const onResolveOptionIDGroupIndex = useCallback(
		(optionID: string) => optionIDGroupIndexMap.get(optionID) ?? Number.POSITIVE_INFINITY,
		[optionIDGroupIndexMap],
	);

	/**
	 * active な option-list に絞り込む
	 */
	const filteredActiveOptionList = useMemo(
		() =>
			filteredOptionList
				/**
				 * 同じ開始時間のものをグループ化し、その中で時間の長さでソート
				 *
				 * groupByOptionID が true のときは、まず optionID（=色）ごとにまとめ、
				 * その中で開始時間順 → 長さ降順 でソートする。
				 */
				.sort((a, b) => {
					if (groupByOptionID) {
						// 色（オプション）ごとにまとめる
						const groupCompare =
							onResolveOptionIDGroupIndex(a.optionID) - onResolveOptionIDGroupIndex(b.optionID);
						if (groupCompare !== 0) return groupCompare;
					}

					// まず開始時間でソート
					const startTimeCompare = onSortOptionStartAt(a.optionStartAt, b.optionStartAt);
					if (startTimeCompare !== 0) return startTimeCompare;

					// 同じ開始時間の場合は、時間の長さで降順ソート
					const aLength = onCalcHeightForOptionPanelItem(a.optionStartAt, a.optionEndAt);
					const bLength = onCalcHeightForOptionPanelItem(b.optionStartAt, b.optionEndAt);
					return bLength - aLength; // 降順（長い順）にソート
				}),
		[
			onSortOptionStartAt,
			onCalcHeightForOptionPanelItem,
			filteredOptionList,
			groupByOptionID,
			onResolveOptionIDGroupIndex,
		],
	);

	const onFilterLeftItems = useCallback(
		(prevItems: Array<OptionWithStartAndEndLeft>, option: OptionWithStartAndEnd) =>
			prevItems.filter((item) => {
				if (item.optionStartAt === option.optionStartAt) {
					return true;
				}

				if (item.optionStartAt < option.optionStartAt && option.optionStartAt < item.optionEndAt) {
					if (onCalcDiffHours(item.optionStartAt, option.optionStartAt) <= 0.5) {
						return true;
					}

					if (item.left < 2) {
						return true;
					}
				}
				return false;
			}),
		[onCalcDiffHours],
	);

	/**
	 * リストから何px右側にずれるかを計算したオプションのリスト
	 *
	 * ※ 計算の数を減らすために、`left` だけ先に別で計算している
	 *
	 * groupByOptionID が true のときは、同じ色（optionID）のアイテムだけで重なり判定し、
	 * グループごとに baseOffset を加算してレーンを分ける。
	 */
	const filteredActiveOptionListWithLeft = useMemo(() => {
		if (!groupByOptionID) {
			const result: Array<OptionWithStartAndEndLeft> = [];
			for (const option of filteredActiveOptionList) {
				const tabSize = onFilterLeftItems(result, option).length;
				result.push({ ...option, left: tabSize * Constants.LeftOffset });
			}
			return result;
		}

		// 前提: filteredActiveOptionList は既に optionID 順にソート済み
		const result: Array<OptionWithStartAndEndLeft> = [];
		let baseOffset = 0;
		let groupResult: Array<OptionWithStartAndEndLeft> = [];
		let groupMaxRelativeLeft = 0;
		let currentOptionID: string | null = null;

		const closeGroup = () => {
			if (currentOptionID === null) return;
			// 次グループの開始位置を確保（このグループ内の最大ズレ + 1 レーン分）
			baseOffset += groupMaxRelativeLeft + Constants.LeftOffset;
			groupResult = [];
			groupMaxRelativeLeft = 0;
		};

		for (const option of filteredActiveOptionList) {
			if (currentOptionID !== option.optionID) {
				closeGroup();
				currentOptionID = option.optionID;
			}
			const tabSize = onFilterLeftItems(groupResult, option).length;
			const relativeLeft = tabSize * Constants.LeftOffset;
			// グループ内の次アイテムの重なり判定は relativeLeft（baseOffset 除外）で行う
			groupResult.push({ ...option, left: relativeLeft });
			result.push({ ...option, left: baseOffset + relativeLeft });
			if (relativeLeft > groupMaxRelativeLeft) {
				groupMaxRelativeLeft = relativeLeft;
			}
		}

		return result;
	}, [filteredActiveOptionList, onFilterLeftItems, groupByOptionID]);

	/**
	 * タイムラインの線（時間グリッド）が、パネルの右端まで届くようにするための最小幅。
	 * パネルの右端 = item.left + PanelLeftBaseOffset + PanelWidth で決まる（panel.tsx と同期）。
	 */
	const panelAreaMinWidth = useMemo(
		() =>
			filteredActiveOptionListWithLeft.reduce(
				(max, item) => Math.max(max, item.left + Constants.PanelLeftBaseOffset + Constants.PanelWidth),
				Constants.TimelineDefaultMinWidth,
			),
		[filteredActiveOptionListWithLeft],
	);

	return useMemo((): Result => {
		return {
			viewData: {
				timelinePanelItemList: filteredActiveOptionList.map((option, index) => {
					const {
						optionStartAt,
						optionEndAt,
						memo,
						cardID,
						optionID,
						uniqueID,
						guestName,
						subOptionName,
						amount,
						roomNum,
					} = option;

					const left = filteredActiveOptionListWithLeft.find((item) => item.uniqueID === uniqueID)?.left ?? 0;

					const guestTotalNumberText = () => {
						if (!roomNum) return null;

						const adultNum = roomNum.reduce((acc, curr) => acc + curr.adultNum, 0);

						let totalNumberText = `大人 ${adultNum}人`;

						const kidsNum = roomNum.reduce((acc, curr) => acc + curr.kidsNum.length, 0);
						if (kidsNum > 0) {
							// 子どもが存在する場合
							totalNumberText = `${totalNumberText}, 子ども ${kidsNum}人`;
						}
						const kidsCosleepingNum = roomNum.reduce(
							(acc, curr) => acc + curr.kidsNum.filter((kid) => kid.asCosleeping).length,
							0,
						);
						if (kidsCosleepingNum > 0) {
							// 添い寝の子どもが存在する場合
							totalNumberText = `${totalNumberText} 子ども ${kidsCosleepingNum}人 ※添い寝（${kidsCosleepingNum}人）`;
						}

						return totalNumberText || null;
					};

					return {
						option,
						optionId: optionID,
						cardId: cardID ?? `${cardID}-${index}`,
						guestName: `${guestName} 様`,
						memo: memo ?? "",
						color: optionColors.find((color) => color.optionID === optionID)?.color ?? "",
						height: onCalcHeightForOptionPanelItem(optionStartAt, optionEndAt),
						top: onCalcTopForOptionPanelItem(optionStartAt),
						left,
						subOptionName: subOptionName || null,
						amount: amount || null,
						guestTotalNumberText: guestTotalNumberText(),
					};
				}),
				activeOptionColorList: optionColors
					.filter(({ optionID: _optionID }) =>
						filteredActiveOptionList.some(({ optionID: __optionID }) => _optionID === __optionID),
					)
					.map(({ color, optionName }) => ({
						color,
						optionNameText: optionName,
					})),
				selectedOption: options.find(({ cardID }) => cardID === selectedCardID),
				firstOptionStartAt: firstOptionStartAt ?? null,
				lastOptionEndAt: lastOptionEndAt ?? null,
				panelAreaMinWidth,
			},
		};
	}, [
		filteredActiveOptionList,
		filteredActiveOptionListWithLeft,
		firstOptionStartAt,
		lastOptionEndAt,
		onCalcHeightForOptionPanelItem,
		onCalcTopForOptionPanelItem,
		optionColors,
		options,
		selectedCardID,
		panelAreaMinWidth,
	]);
};
